// Continuous-forecast preview colors: dashed orange q50 line (accent-500 / accent-400)
// over a teal q10-q90 interval band (brand-300 / brand-400).
export const CONTINUOUS_PREVIEW_COLOR = { light: '#ff7926', dark: '#ff9151' };
export const CONTINUOUS_PREVIEW_BAND_COLOR = { light: '#74cdd6', dark: '#3aabb8' };

// Append an alpha channel to a hex or hsl color (mirrors ForecastComparisonChart).
const addTransparency = (color, alpha = 0.1) => {
  if (typeof color === 'string' && color.startsWith('#')) {
    const alphaHex = Math.round(alpha * 255).toString(16).padStart(2, '0');
    return `${color}${alphaHex}`;
  }
  if (typeof color === 'string' && color.startsWith('hsl')) {
    return color.replace('hsl', 'hsla').replace(')', `, ${alpha})`);
  }
  return color;
};

/**
 * Return the forecaster's continuous-forecast rows for charting — the full,
 * unmasked series. The dashboard toggle decides whether the submission series
 * is shown alongside it. Rows without a usable datetime are dropped so they
 * don't become {x: undefined} points on the time axis.
 *
 * @param {Array<{datetime:string, variable:string, value:number}>} continuousForecastData
 * @returns {Array} rows with a datetime (same shape as the input)
 */
export const buildContinuousPreview = (continuousForecastData) => {
  const continuous = Array.isArray(continuousForecastData) ? continuousForecastData : [];
  return continuous.filter((row) => Boolean(row.datetime));
};

/**
 * Build Chart.js datasets for the preview: a dashed q50 line plus a shaded
 * q10–q90 band. Returns [] when there is nothing to draw. The lower-bound
 * dataset is emitted immediately before the upper-bound dataset so its
 * `fill: '+1'` shades the band between them.
 *
 * @param {Array} previewRows rows from buildContinuousPreview
 * @param {{effectiveDark?:boolean, quantile?:string, lowerQuantile?:string, upperQuantile?:string}} opts
 * @returns {Array} Chart.js dataset objects
 */
export const buildContinuousPreviewDatasets = (
  previewRows,
  {
    effectiveDark = false,
    quantile = 'q50',
    lowerQuantile = 'q10',
    upperQuantile = 'q90',
  } = {},
) => {
  const rows = Array.isArray(previewRows) ? previewRows : [];
  const byQuantile = (target) =>
    rows
      .filter((row) => row.variable === target)
      .map((row) => ({ x: row.datetime, y: row.value }));

  const q50 = byQuantile(quantile);
  const lower = byQuantile(lowerQuantile);
  const upper = byQuantile(upperQuantile);

  if (q50.length === 0 && lower.length === 0 && upper.length === 0) return [];

  const color = effectiveDark
    ? CONTINUOUS_PREVIEW_COLOR.dark
    : CONTINUOUS_PREVIEW_COLOR.light;
  const bandColor = effectiveDark
    ? CONTINUOUS_PREVIEW_BAND_COLOR.dark
    : CONTINUOUS_PREVIEW_BAND_COLOR.light;
  const datasets = [];

  if (q50.length > 0) {
    datasets.push({
      label: 'Continuous forecast',
      data: q50,
      borderColor: color,
      backgroundColor: addTransparency(color, 0.1),
      tension: 0.4,
      pointRadius: 0,
      borderWidth: 2,
      borderDash: [5, 5],
      fill: false,
    });
  }

  if (lower.length > 0) {
    datasets.push({
      label: 'Continuous band (q10)',
      data: lower,
      borderColor: bandColor,
      backgroundColor: addTransparency(bandColor, 0.1),
      borderWidth: 1,
      borderDash: [5, 5],
      pointRadius: 0,
      tension: 0.4,
      fill: upper.length > 0 ? '+1' : false,
    });
  }

  if (upper.length > 0) {
    datasets.push({
      label: 'Continuous band (q90)',
      data: upper,
      borderColor: bandColor,
      borderWidth: 1,
      borderDash: [5, 5],
      pointRadius: 0,
      tension: 0.4,
      fill: false,
    });
  }

  return datasets;
};
